/**
 * Схеми сигнального протоколу.
 *
 * Джерело правди — `server/var/www/webrtcapp/main.py`, а не цей файл. Тут лише
 * віддзеркалення чинного контракту: сервер розбирає сам тільки `hello` і
 * `safari_ready`, решту пересилає дослівно, тож на іншому кінці кімнати може
 * бути мобільний клієнт річної давнини або бот. Формі повідомлення довіряти не
 * можна — звідси zod, а не інтерфейси «на віру».
 *
 * ⚠️ SDP та ICE розрізняються НАЯВНІСТЮ КЛЮЧА (`offer` / `answer` / `candidate`),
 * а не полем `type`. Це не примха: так їх шлють мобільні клієнти зі стору.
 */
import { z } from 'zod';

/** Ідентифікатор кімнати, як його валідує сервер. */
export const roomIdSchema = z
  .string()
  .min(1)
  .regex(/^[a-zA-Z0-9-]+$/u, 'room id: дозволені лише латиниця, цифри й дефіси');

const sessionDescriptionSchema = z.object({
  sdp: z.string(),
  type: z.enum(['offer', 'answer', 'pranswer', 'rollback']),
});

const iceCandidateSchema = z.object({
  candidate: z.string(),
  sdpMid: z.string().nullish(),
  sdpMLineIndex: z.number().nullish(),
  usernameFragment: z.string().nullish(),
});

/** Повідомлення сервера: призначення ініціатора. Клієнт не обирає роль сам. */
export const initiatorAssignmentSchema = z.object({
  type: z.literal('server_initiator_assignment'),
  initiatorId: z.string().min(1),
  timestamp: z.number().optional(),
  reason: z.string().optional(),
});

/**
 * Повідомлення сервера про приєднання другого учасника.
 *
 * ⚠️ Назва оманлива: сервер шле це, коли в кімнаті стало більше одного учасника,
 * а не коли повернувся той самий `client_id`. Тому поява нового учасника завжди
 * означає перестворення зʼєднання обома сторонами, навіть якщо поточне виглядає
 * робочим.
 */
export const participantReconnectedSchema = z.object({
  type: z.literal('participant_reconnected'),
  clientId: z.string().min(1),
  timestamp: z.number().optional(),
  remaining_participants: z.number().optional(),
});

export const participantLeftSchema = z.object({
  type: z.literal('participant_left'),
  clientId: z.string().min(1),
  reason: z.string().optional(),
  timestamp: z.number().optional(),
  remaining_participants: z.number().optional(),
});

export const helloSchema = z.object({
  type: z.literal('hello'),
  clientId: z.string().min(1),
  platform: z.string().optional(),
});

export const safariReadySchema = z.object({
  type: z.literal('safari_ready'),
  clientId: z.string().min(1),
  platform: z.string().optional(),
});

export const cameraSchema = z.object({
  type: z.literal('camera'),
  clientId: z.string().min(1).optional(),
  facing: z.string().optional(),
});

export const deviceInfoSchema = z.object({
  type: z.literal('device_info'),
  clientId: z.string().min(1).optional(),
  deviceType: z.string().optional(),
});

export const mediaStateSchema = z.object({
  type: z.literal('media_state'),
  clientId: z.string().min(1).optional(),
  micEnabled: z.boolean().optional(),
  videoEnabled: z.boolean().optional(),
  speakerOn: z.boolean().optional(),
  cameraFacing: z.string().optional(),
});

/** Команда віддаленого керування. Веб її отримує, але виконати може не все. */
export const remoteControlSchema = z.object({
  type: z.literal('remote_control'),
  clientId: z.string().min(1).optional(),
  action: z.string().optional(),
  value: z.unknown().optional(),
});

const typedMessageSchema = z.discriminatedUnion('type', [
  initiatorAssignmentSchema,
  participantReconnectedSchema,
  participantLeftSchema,
  helloSchema,
  safariReadySchema,
  cameraSchema,
  deviceInfoSchema,
  mediaStateSchema,
  remoteControlSchema,
]);

const offerSchema = z.object({
  offer: sessionDescriptionSchema,
  clientId: z.string().min(1).optional(),
});

const answerSchema = z.object({
  answer: sessionDescriptionSchema,
  clientId: z.string().min(1).optional(),
});

const candidateSchema = z.object({
  candidate: iceCandidateSchema,
  clientId: z.string().min(1).optional(),
});

export type SignalingMessage =
  | ({ kind: 'offer' } & z.infer<typeof offerSchema>)
  | ({ kind: 'answer' } & z.infer<typeof answerSchema>)
  | ({ kind: 'candidate' } & z.infer<typeof candidateSchema>)
  | ({ kind: 'typed' } & z.infer<typeof typedMessageSchema>);

export type ParseOutcome =
  /** Повідомлення розібране й придатне до обробки. */
  | { status: 'ok'; message: SignalingMessage }
  /**
   * Тип відомий протоколу, але не цьому клієнту, або це наше власне ехо.
   * Протокол розширюваний — мовчки ігноруємо.
   */
  | { status: 'ignored'; reason: string }
  /**
   * Форма зламана. Відхиляємо й логуємо, але сокет НЕ закриваємо:
   * чужий баг не має класти наш дзвінок.
   */
  | { status: 'invalid'; reason: string };

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

/**
 * Розбирає сире текстове повідомлення сигналінгу.
 *
 * Порядок перевірок повторює порядок на боці мобільного клієнта: спершу
 * наявність ключів `offer`/`answer`/`candidate`, і лише потім поле `type`.
 */
export function parseSignalingMessage(raw: string): ParseOutcome {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { status: 'invalid', reason: 'не JSON' };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { status: 'invalid', reason: 'очікувався обʼєкт' };
  }

  const record = parsed as Record<string, unknown>;

  if (hasOwn(record, 'offer')) {
    const result = offerSchema.safeParse(record);
    return result.success
      ? { status: 'ok', message: { kind: 'offer', ...result.data } }
      : { status: 'invalid', reason: `offer: ${result.error.issues[0]?.message ?? 'невірна форма'}` };
  }

  if (hasOwn(record, 'answer')) {
    const result = answerSchema.safeParse(record);
    return result.success
      ? { status: 'ok', message: { kind: 'answer', ...result.data } }
      : {
          status: 'invalid',
          reason: `answer: ${result.error.issues[0]?.message ?? 'невірна форма'}`,
        };
  }

  if (hasOwn(record, 'candidate')) {
    const result = candidateSchema.safeParse(record);
    return result.success
      ? { status: 'ok', message: { kind: 'candidate', ...result.data } }
      : {
          status: 'invalid',
          reason: `candidate: ${result.error.issues[0]?.message ?? 'невірна форма'}`,
        };
  }

  if (!hasOwn(record, 'type')) {
    return { status: 'ignored', reason: 'без типу і без ключів SDP/ICE' };
  }

  if (typeof record['type'] !== 'string') {
    return { status: 'invalid', reason: 'type має бути рядком' };
  }

  const result = typedMessageSchema.safeParse(record);
  if (result.success) {
    return { status: 'ok', message: { kind: 'typed', ...result.data } };
  }

  const known = typedMessageSchema.options.some(
    (option) => option.shape.type.value === record['type'],
  );

  return known
    ? {
        status: 'invalid',
        reason: `${record['type']}: ${result.error.issues[0]?.message ?? 'невірна форма'}`,
      }
    : { status: 'ignored', reason: `невідомий тип «${record['type']}»` };
}
